import { useEffect, useRef, useState } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import { usePushBackHandler } from '../../context/BackStackContext'
import { Button } from './Button'
import { IconButton } from './IconButton'
import { Pressable } from './Pressable'
import { TextInput } from './TextInput'
import type { StockScanDirection } from '../../services/InventoryScannerService'
import {
  isCameraPermissionDenied,
  isNativeScannerAvailable,
  startContinuousScan,
  stopScan,
} from '../../services/native/barcodeScannerService'
import { useNls } from '../../nls/nlsProvider'

export type BarcodeScannerShellProps = {
  open: boolean
  onClose: () => void
  onScan: (barcode: string, action: StockScanDirection) => void
  mode?: 'INVENTORY' | 'COUNT'
}

export const BarcodeScannerShell = ({
  open,
  onClose,
  onScan,
  mode = 'INVENTORY',
}: BarcodeScannerShellProps) => {
  const { t } = useNls()

  const handleClose = () => {
    stopScan().catch(() => {})
    onClose()
  }

  usePushBackHandler(handleClose, open)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [manualCode, setManualCode] = useState('')
  const [scanAction, setScanAction] = useState<StockScanDirection>('OUT')
  const [error, setError] = useState('')
  const [isNative, setIsNative] = useState(false)

  useEffect(() => {
    isNativeScannerAvailable().then(setIsNative).catch(() => setIsNative(false))
  }, [])

  useEffect(() => {
    if (!open) return
    let cancelled = false

    const handleScannedBarcode = (text: string) => {
      if (cancelled) return
      stopScan().catch(() => {})
      onScan(text, scanAction)
    }

    startContinuousScan(handleScannedBarcode, {
      videoElement: videoRef.current,
    })
      .then(() => {
        if (!cancelled) setError('')
      })
      .catch((caught) => {
        if (cancelled) return
        if (isCameraPermissionDenied(caught)) {
          setError(t('NLSBarcodeScannerPermissionDenied'))
        } else {
          setError(caught instanceof Error ? caught.message : t('NLSBarcodeScannerCameraFailed'))
        }
      })

    return () => {
      cancelled = true
      stopScan().catch(() => {})
    }
  }, [onScan, open, scanAction, t])

  if (!open) return null

  return (
    <div
      className={`fixed inset-0 z-[120] ${isNative ? 'bg-transparent' : 'bg-[color-mix(in_srgb,var(--bg)_85%,transparent)] backdrop-blur-2xl'} flex items-center justify-center p-4`}
    >
      <section className="w-full max-w-md rounded-[28px] border border-[var(--border-subtle)] bg-[var(--surface-card)] shadow-[var(--shadow-pop)] overflow-hidden text-[var(--text-strong)]">
        <header className="flex items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-2xl border border-[color-mix(in_srgb,var(--accent)_25%,transparent)] bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] text-[var(--accent-strong)]">
              <QrCodeScannerRoundedIcon fontSize="small" />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{t('NLSBarcodeScannerEyebrow')}</p>
              <h2 className="text-sm font-semibold text-[var(--text-strong)]">
                {mode === 'COUNT' ? t('NLSBarcodeScannerCountTitle') : t('NLSBarcodeScannerInventoryTitle')}
              </h2>
            </div>
          </div>
          <IconButton
            variant="tertiary"
            size="md"
            label={t('NLSBarcodeScannerCloseAria')}
            icon={<CloseRoundedIcon fontSize="small" />}
            onClick={handleClose}
          />
        </header>

        <div className={`relative aspect-[3/4] ${isNative ? 'bg-transparent' : 'bg-[var(--bg)]'}`}>
          {!isNative ? (
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
          ) : (
            <div className="h-full w-full bg-transparent" />
          )}
          <div className="pointer-events-none absolute inset-6 rounded-[24px] border border-[var(--accent)] shadow-[0_0_0_999px_rgba(2,6,23,0.5)]" /> {/* mosaic-keep: camera overlay */}
          <div className="pointer-events-none absolute left-8 right-8 top-1/2 h-px bg-[var(--accent)] shadow-[0_0_18px_var(--accent)]" />
        </div>

        <form
          className="flex flex-col gap-2 p-4"
          onSubmit={(event) => {
            event.preventDefault()
            const value = manualCode.trim()
            if (!value) return
            onScan(value, scanAction)
          }}
        >
          {mode === 'INVENTORY' ? (
            <div className="grid grid-cols-2 gap-2">
              {(['OUT', 'IN'] as const).map((action) => (
                <Pressable
                  key={action}
                  aria-pressed={scanAction === action}
                  className={`h-10 rounded-2xl border text-xs font-semibold ${
                    scanAction === action
                      ? 'border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_25%,transparent)] text-[var(--accent-strong)]'
                      : 'border-[var(--border-subtle)] bg-[var(--surface-muted)] text-[var(--muted)]'
                  }`}
                  onClick={() => setScanAction(action)}
                >
                  {action === 'OUT' ? t('NLSBarcodeScannerScanOut') : t('NLSBarcodeScannerScanIn')}
                </Pressable>
              ))}
            </div>
          ) : null}
          {error ? (
            <p className="rounded-xl border border-[var(--warning)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-3 py-2 text-xs text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
              {error}
            </p>
          ) : null}
          <label className="flex flex-col gap-1 text-xs text-[var(--muted)]">
            {t('NLSBarcodeScannerManualLabel')}
            <TextInput
              value={manualCode}
              onChange={setManualCode}
              placeholder={t('NLSBarcodeScannerManualPlaceholder')}
            />
          </label>
          <Button type="submit" variant="primary" size="lg">
            {t('NLSBarcodeScannerUseBarcode')}
          </Button>
        </form>
      </section>
    </div>
  )
}
